import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertColor, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth } from '../../firebaseConfig';
import AuthLayout from './AuthLayout';
import PasswordField from './PasswordField';
import { sendEmailVerificationProfessional } from '../../graphqlService';
import { useAuth } from '../../context/AuthContext';
import { rateLimitManager } from '../../services/rateLimitManager';
import { authMonitoringService } from '../../services/authMonitoringService';
import { debugLog } from '../../utils/debugLog';
import { pluralize } from '../../utils/format';
import { findRateLimitMessage, rateLimitMessageFromError } from '../../utils/rateLimitMessage';
import { getSafeRedirect } from '../../utils/safeRedirect';
import { AuthErrorAction, describeAuthError } from './authErrors';
import { FIELD_ERRORS, FieldErrors, focusFirstInvalid, normalizeEmail, SUPPORT_HREF, validateEmail } from './authForm';

type LoginField = 'email' | 'password';
const FIELD_ORDER: readonly LoginField[] = ['email', 'password'];

interface Feedback {
  severity: AlertColor;
  message: string;
  action?: AuthErrorAction;
}

/** An account that signed in but hasn't verified its email yet. */
interface Unverified {
  email: string;
  status: 'idle' | 'sending' | 'sent';
  /** Why the last resend didn't go out, shown in the same message. */
  problem?: { severity: 'warning' | 'error'; message: string };
}

const RESEND_FAILED_MESSAGE = "We couldn't send the verification email. Try again in a few minutes.";

const blockedMessage = (resetTime?: number) => {
  if (!resetTime) return 'Too many sign-in attempts. Wait a few minutes, or reset your password.';
  const minutes = Math.max(1, Math.ceil((resetTime - Date.now()) / 60_000));
  return `Too many sign-in attempts. Try again in ${pluralize(minutes, 'minute')}, or reset your password.`;
};

/** Appended to a failed sign-in once the client-side limit is close. */
const attemptsNote = (remaining: number) =>
  remaining <= 0
    ? 'No attempts left for now. Wait a few minutes, or reset your password.'
    : `${pluralize(remaining, 'attempt')} left before sign-in is paused.`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<LoginField>>({});
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [unverified, setUnverified] = useState<Unverified | null>(null);
  const [loading, setLoading] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();

  // Pages that send people here with something to say: the verification
  // link landing ("Email verified...") and "request a new link".
  useEffect(() => {
    const state = location.state as { message?: string; emailVerified?: boolean } | null;
    if (state?.message) {
      setFeedback({ severity: state.emailVerified ? 'success' : 'info', message: state.message });
    }
  }, [location.state]);

  useEffect(() => {
    // Wait for AuthContext to publish the signed-in user before navigating.
    // Navigating on sign-in success alone races onAuthStateChanged: PrivateRoute
    // still sees user=null and bounces straight back to /login.
    if (shouldNavigate && authUser) {
      // Return-to support, e.g. an existing user sent here to sign in before
      // accepting an editor invitation. Only same-origin app paths are
      // honoured: "/x" but never "//evil.com" or "https://...", which would
      // make this an open redirect.
      const redirectTarget = getSafeRedirect(location.search);
      if (redirectTarget) {
        navigate(redirectTarget, { replace: true });
        return;
      }

      if (localStorage.getItem('projectId')) {
        const projectId = localStorage.getItem('projectId');
        navigate(`/edit-projects/${projectId}`);
        localStorage.removeItem('projectId');
      } else {
        debugLog('Attempting to navigate to /dashboard');
        navigate('/dashboard');
      }
    }
  }, [shouldNavigate, authUser, navigate, location.search]);

  const clearFieldError = (field: LoginField) => {
    if (fieldErrors[field]) setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;

    setFeedback(null);
    setUnverified(null);

    const errors: FieldErrors<LoginField> = {};
    const emailProblem = validateEmail(email);
    if (emailProblem) errors.email = emailProblem;
    if (!password) errors.password = FIELD_ERRORS.passwordRequired;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(FIELD_ORDER, errors, { email: emailRef, password: passwordRef });
      return;
    }

    const normalizedEmail = normalizeEmail(email);

    // Check rate limiting before attempting sign-in
    const rateLimitResult = rateLimitManager.checkRateLimit(normalizedEmail, 'login');
    if (!rateLimitResult.allowed) {
      authMonitoringService.logRateLimitTrigger(normalizedEmail, 'login', 5);
      setFeedback({ severity: 'warning', message: blockedMessage(rateLimitResult.resetTime), action: 'reset-password' });
      return;
    }
    const remainingNote =
      rateLimitResult.attemptsRemaining <= 2 ? attemptsNote(rateLimitResult.attemptsRemaining) : null;

    authMonitoringService.logLoginAttempt(normalizedEmail, 'user_form');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;

      // Record successful sign-in (clears rate limiting)
      rateLimitManager.recordSuccess(normalizedEmail, 'login');
      authMonitoringService.logLoginSuccess(normalizedEmail, 'email_password');

      // AuthContext keeps an unverified user out of the app but leaves the
      // Firebase session in place, which is what Resend below sends from.
      if (!user.emailVerified) {
        setUnverified({ email: user.email || normalizedEmail, status: 'idle' });
        return;
      }

      // The ID token used to be copied into localStorage here. Nothing read
      // it, and it outlived the session; Firebase keeps the session itself.
      debugLog('Setting shouldNavigate to true');
      setShouldNavigate(true);
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      const copy = describeAuthError(error, 'sign-in');
      authMonitoringService.logLoginFailure(normalizedEmail, firebaseError, 'login_form');

      if (copy.field) {
        const fieldError = { [copy.field]: copy.message } as FieldErrors<LoginField>;
        setFieldErrors(fieldError);
        focusFirstInvalid(FIELD_ORDER, fieldError, { email: emailRef, password: passwordRef });
      } else {
        setFeedback({
          severity: copy.severity,
          message: remainingNote ? `${copy.message} ${remainingNote}` : copy.message,
          action: copy.action,
        });
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('Sign-in error:', firebaseError?.code, firebaseError?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverified || unverified.status === 'sending') return;
    const target = unverified.email;
    // Applies an update only while the message is still about the same
    // address: a new sign-in attempt during the request replaces it.
    const update = (next: Partial<Unverified>) =>
      setUnverified((current) => (current && current.email === target ? { ...current, ...next } : current));

    const currentUser = auth.currentUser;
    if (!currentUser) {
      update({
        status: 'idle',
        problem: { severity: 'warning', message: 'Your session ended. Sign in again, then resend the email.' },
      });
      return;
    }

    update({ status: 'sending', problem: undefined });
    try {
      // Backend generates secure tokens; this param is kept for backward compatibility.
      // It only sends to the signed-in account's own address, which can
      // differ from the field if it was edited after the failed sign-in.
      const result = await sendEmailVerificationProfessional(currentUser.email || target, 'unused');

      // There is no Firebase fallback on this page, but a rate limit should
      // still read as "wait", not as a generic failure.
      const rateLimited = findRateLimitMessage(result?.error, result?.message);
      if (result?.success) {
        update({ status: 'sent', problem: undefined });
      } else {
        update({
          status: 'idle',
          problem: rateLimited
            ? { severity: 'warning', message: rateLimited }
            : { severity: 'error', message: RESEND_FAILED_MESSAGE },
        });
      }
    } catch (error: unknown) {
      console.error('Error sending verification email:', error);
      // A limit raised as a GraphQL error (or the API's HTTP 429) used to be
      // reported as a generic failure, which invites an immediate retry.
      const rateLimited = rateLimitMessageFromError(error);
      update({
        status: 'idle',
        problem: rateLimited
          ? { severity: 'warning', message: rateLimited }
          : { severity: 'error', message: RESEND_FAILED_MESSAGE },
      });
    }
  };

  const feedbackAction = (action?: AuthErrorAction) => {
    if (action === 'reset-password') {
      return (
        <Button component={RouterLink} to="/forgot-password" color="inherit" size="small">
          Reset password
        </Button>
      );
    }
    if (action === 'contact-support') {
      return (
        <Button href={SUPPORT_HREF} color="inherit" size="small">
          Contact support
        </Button>
      );
    }
    return undefined;
  };

  const renderUnverified = (state: Unverified) => {
    const sent = state.status === 'sent';
    const severity: AlertColor = state.problem ? state.problem.severity : sent ? 'success' : 'warning';
    const message = state.problem
      ? state.problem.message
      : sent
        ? `We sent a new verification link to ${state.email}. Open it, then sign in again.`
        : `Verify your email to sign in. Open the link we sent to ${state.email}, or send a new one.`;
    return (
      <Alert severity={severity} sx={{ mb: 3, '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}>
        {message}
        {/* Under the text rather than beside it, so a long address still fits on a phone. */}
        {!sent && (
          <Box sx={{ mt: 1.25 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleResendVerification}
              disabled={state.status === 'sending'}
            >
              {state.status === 'sending' ? 'Sending…' : 'Resend email'}
            </Button>
          </Box>
        )}
      </Alert>
    );
  };

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back to PithPlay.">
      {unverified
        ? renderUnverified(unverified)
        : feedback && (
            <Alert
              severity={feedback.severity}
              role={feedback.severity === 'success' || feedback.severity === 'info' ? 'status' : 'alert'}
              sx={{ mb: 3, '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}
              action={feedbackAction(feedback.action)}
            >
              {feedback.message}
            </Alert>
          )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 2.5 }}>
        <TextField
          inputRef={emailRef}
          id="email"
          name="email"
          type="email"
          label="Email address"
          autoComplete="email"
          autoFocus
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            clearFieldError('email');
          }}
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email}
        />

        <Box>
          <PasswordField
            ref={passwordRef}
            id="password"
            name="password"
            label="Password"
            autoComplete="current-password"
            required
            InputLabelProps={{ required: false }}
            fullWidth
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearFieldError('password');
            }}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password}
          />
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ fontWeight: 600 }}>
              Forgot your password?
            </Link>
          </Box>
        </Box>

        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 0.5 }}>
          {loading ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
              Signing in…
            </>
          ) : (
            'Sign in'
          )}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
          New to PithPlay?{' '}
          <Link component={RouterLink} to="/signup" sx={{ fontWeight: 600 }}>
            Create an account
          </Link>
        </Typography>
      </Box>
    </AuthLayout>
  );
};

export default Login;
